'use client'

import { Suspense, useState } from 'react'
import { postLoginDestination } from '@/lib/post-login'
import { signIn, getSession } from 'next-auth/react'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { Button, Input, Label } from '@/components/ui'
import {
    AuthShell,
    AuthHeader,
    AuthErrorBanner,
    FieldError,
    PasswordField,
    CheckboxField,
} from '@/components/auth'
import { authErrorMessage } from '@/lib/auth-errors'
import { Loader2 } from 'lucide-react'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function LoginForm() {
    const searchParams = useSearchParams()
    const callbackUrl = searchParams.get('callbackUrl') || '/'

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [rememberMe, setRememberMe] = useState(false)
    const [emailError, setEmailError] = useState('')
    // NextAuth redirect-based failures land here as ?error=<code>; without this
    // the code used to be silently dropped.
    const [errorCode, setErrorCode] = useState(searchParams.get('error') ?? '')
    const [loading, setLoading] = useState(false)

    const rateLimited = errorCode === 'RATE_LIMITED'

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setEmailError('')
        setErrorCode('')

        if (!EMAIL_PATTERN.test(email.trim())) {
            setEmailError('Please enter a valid email address, like you@example.org.')
            return
        }

        setLoading(true)
        try {
            const result = await signIn('credentials', {
                email,
                password,
                rememberMe: String(rememberMe),
                redirect: false,
            })

            if (result?.error) {
                setErrorCode(result.error)
            } else {
                // Tenant subdomain in production, path on localhost/previews;
                // a super admin goes to the back office (src/lib/post-login.ts).
                const session = await getSession()
                window.location.href = postLoginDestination(
                    { role: session?.user?.role, tenantSlug: session?.user?.tenantSlug },
                    window.location.hostname,
                    callbackUrl
                )
                return // keep the button in its loading state through the redirect
            }
        } catch {
            setErrorCode('UNEXPECTED')
        }
        setLoading(false)
    }

    return (
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4.5">
            {errorCode && <AuthErrorBanner>{authErrorMessage(errorCode)}</AuthErrorBanner>}

            <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                    id="email"
                    type="email"
                    placeholder="you@example.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    aria-invalid={emailError ? true : undefined}
                    aria-describedby={emailError ? 'email-error' : undefined}
                    className={emailError ? 'border-[var(--border-danger)]' : undefined}
                />
                {emailError && <FieldError id="email-error">{emailError}</FieldError>}
            </div>

            <PasswordField
                id="password"
                label="Password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                labelEnd={
                    <Link
                        href="/forgot-password"
                        className="text-sm font-medium text-[var(--text-brand)] hover:underline"
                    >
                        Forgot password?
                    </Link>
                }
            />

            {/* The locked design swaps the checkbox row for a reset CTA */}
            {!rateLimited && (
                <CheckboxField
                    id="remember-me"
                    label="Keep me signed in"
                    checked={rememberMe}
                    onChange={setRememberMe}
                />
            )}

            <div className="mt-1.5 flex flex-col gap-3">
                <Button type="submit" size="lg" className="w-full" disabled={loading}>
                    {loading ? (
                        <>
                            <Loader2 className="animate-spin" />
                            Signing in...
                        </>
                    ) : (
                        'Sign in'
                    )}
                </Button>
                {rateLimited && (
                    <Button asChild variant="secondary" size="lg" className="w-full">
                        <Link href="/forgot-password">Reset password</Link>
                    </Button>
                )}
            </div>

            <p className="mt-1.5 text-center text-sm leading-5 text-[var(--text-tertiary)]">
                New to KehillaHQ? Your synagogue administrator will send you an invitation to join.
            </p>
        </form>
    )
}

export default function LoginPage() {
    return (
        <AuthShell>
            <AuthHeader title="Welcome back" subtitle="Sign in to your KehillaHQ account." />
            <Suspense fallback={<div className="h-72 animate-pulse rounded-lg bg-[var(--bg-subtle)]" />}>
                <LoginForm />
            </Suspense>
        </AuthShell>
    )
}
